import React from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { CRMPageWrap } from "../../components/Simple/CRMPageWrap/CRMPageWrap";
import { CRMLegalMenuBarContainer } from "../../containers/CRMLegalMenuBarContainer/CRMLegalMenuBarContainer";
import { CRMHomesMenuBarContainer } from "../../containers/CRMHomesMenuBarContainer/CRMHomesMenuBarContainer";
import { CRMProbateMenuBarContainer } from "../../containers/CRMProbateMenuBarContainer/CRMProbateMenuBarContainer";
import { Background } from "../../components/BuildingBlocks/Background";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMPadding } from "../../components/Simple/CRMPadding/CRMPadding";
import { CRMSpacingColumn } from "../../components/CRMSpacingColumn/CRMSpacingColumn";
import NotFoundPage from "../NotFoundPage/NotFoundPage";
import { findProduct, TGuideProduct, TGuideProductId } from "../../guides/manifest";
import { generatedGuides, TGeneratedGuide } from "../../guides/manifest.generated";
import { isGuidesAccessAllowed } from "../CRMGuidesPage/guidesAccess";
import { CRMGuidesHeader } from "../../components/CRM/CRMGuidesHeader/CRMGuidesHeader";
import { fetchMyGuideReads, computeReadState, TGuideReadState } from "../../components/CRM/CRMGuidesMarkAsRead/guideReadsApi";
import { TGuideRead } from "../../../../domain/codecs/GuideRead";

const MenuBarForProduct = (props: { productId: TGuideProductId; containerProps: TContainerStateProps }): JSX.Element => {
    switch (props.productId) {
        case "legal":
            return <CRMLegalMenuBarContainer {...props.containerProps} />;
        case "homes":
            return <CRMHomesMenuBarContainer {...props.containerProps} />;
        case "probate":
            return <CRMProbateMenuBarContainer {...props.containerProps} />;
    }
};

const formatReadDate = (iso: string): string => {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const CardBadge = (props: { state: TGuideReadState }): JSX.Element | null => {
    if (props.state.kind === "unread") return null;
    if (props.state.kind === "read") {
        return (
            <div className="crm-guides-product-page__guide-card-badge crm-guides-product-page__guide-card-badge--read">
                ✓ Read {formatReadDate(props.state.read_at)}
            </div>
        );
    }
    return (
        <div className="crm-guides-product-page__guide-card-badge crm-guides-product-page__guide-card-badge--stale">
            • Updated since you read
        </div>
    );
};

const GuideCard = (props: { guide: TGeneratedGuide; state: TGuideReadState }): JSX.Element => (
    <a
        className="crm-guides-product-page__guide-card"
        href={`/crm/guides/${props.guide.product}/${props.guide.slug}`}
    >
        <CardBadge state={props.state} />
        <div className="crm-guides-product-page__guide-card-title">{props.guide.title}</div>
        <div className="crm-guides-product-page__guide-card-description">{props.guide.one_line_description}</div>
        <div className="crm-guides-product-page__guide-card-cta">Read →</div>
    </a>
);

const ProductBody = (props: { product: TGuideProduct; reads: Array<TGuideRead> }): JSX.Element => {
    const productGuides = generatedGuides.filter((g) => g.product === props.product.id);

    return (
        <CRMPadding size="large">
            <CRMSpacingColumn spacing="large">
                <CRMGuidesHeader
                    title={`${props.product.title} guides`}
                    breadcrumbs={[
                        { label: "Guides", href: "/crm/guides" },
                        { label: props.product.title },
                    ]}
                    backHref="/crm/guides"
                    backLabel="All products"
                />

                {props.product.categories.length === 0
                    ? <p className="crm-guides-product-page__empty">No guides yet. A team-mate will fill this in soon.</p>
                    : props.product.categories.map((category) => {
                        const categoryGuides = productGuides.filter((g) => g.category === category.id);
                        return (
                            <div key={category.id} className="crm-guides-product-page__category">
                                <h2 className="crm-guides-product-page__category-title">{category.title}</h2>
                                <p className="crm-guides-product-page__category-description">{category.description}</p>
                                {categoryGuides.length === 0
                                    ? <p className="crm-guides-product-page__category-empty">No guides yet in this category.</p>
                                    : <div className="crm-guides-product-page__guide-grid">
                                        {categoryGuides.map((g) => {
                                            const state = computeReadState(props.reads, g.product, g.slug, g.content_hash);
                                            return <GuideCard key={g.slug} guide={g} state={state} />;
                                        })}
                                    </div>
                                }
                            </div>
                        );
                    })
                }
            </CRMSpacingColumn>
        </CRMPadding>
    );
};

const CRMGuidesProductPage = (props: TContainerStateProps): JSX.Element => {
    const [reads, setReads] = React.useState<Array<TGuideRead>>([]);

    React.useEffect(() => {
        let cancelled = false;
        fetchMyGuideReads()
            .then((rows) => { if (!cancelled) setReads(rows); })
            .catch((e) => { console.error("[guides] failed to load my reads — cards will render as unread", e); });
        return () => { cancelled = true; };
    }, []);

    if (!isGuidesAccessAllowed(props.state.global.user.user_role)) {
        return <NotFoundPage state={props.state} dispatch={props.dispatch} />;
    }

    const productId = props.state.routes.params.productId;
    const product = productId ? findProduct(productId) : undefined;

    if (!product) {
        return <NotFoundPage state={props.state} dispatch={props.dispatch} />;
    }

    return (
        <CRMPageWrap>
            <MenuBarForProduct productId={product.id} containerProps={props} />
            <Background padding={`${CRMSpacing.X_LARGE} 0px`}>
                <ProductBody product={product} reads={reads} />
            </Background>
        </CRMPageWrap>
    );
};

export default CRMGuidesProductPage;
